import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from "core/hooks/useTranslation";
import useRole from 'core/hooks/useRole';

import { Form, Col, Row, Button } from 'antd';

import Select from 'components/common/select';
import Input from 'components/common/input';

import { MEDIA_TYPE, REDIRECTION_TYPE } from 'constants/campaign.constants';

import autoSuggestionType from 'types/autoSuggestion/autoSuggestion.type';
import { USER_ROLE } from 'core/constants/auth';
import autosuggestionService from 'core/services/apiServices/autosuggestionService';

const FIELD_NAMES = {
    NAME: "name",
    TRAFFIC_SOURCES: "trafficSources",
    CAMPAIGN: "campaignId",
    TYPE: "type",
    CREATIVE: "creative",
    AFFILIATE_ID: "affiliateId",
    URL_TYPE: "urlType",
    REDIRECTION_LINK: "redirectionLink"
}

const MediaCreateForm = ({
    formInstance,
    onCampaignChange,
    onAffiliateChange,
    selectedCreativeData,
    selectedAffiliateId,
    selectedCampaignId,
    campaigns,
    affiliates,
    showLightbox,
    formInitialValues,
    creatives,
    trafficSources,
    onValuesChange
}) => {
    const { t } = useTranslation();
    const [redirectionType, setRedirectionType] = useState(null);

    const { hasRole } = useRole();

    const shouldShowAffiliateField = hasRole(USER_ROLE.AFFILIATE_MANAGER) || hasRole(USER_ROLE.ADMIN);

    const disableCampaignField = (shouldShowAffiliateField && !selectedAffiliateId) || formInitialValues.campaignId;
    const disableTrafficSourceField = shouldShowAffiliateField && !selectedAffiliateId;
    const disableRedirectionLinkField = !selectedCampaignId || redirectionType === null;

    const [redirectionLinkOptions, setRedirectionLinkOptions] = useState([])

    useEffect(() => {
        if(redirectionType === null || !selectedCampaignId){
            return;
        }
        let fn = redirectionType === REDIRECTION_TYPE.WEB ? autosuggestionService.getLandingUrls : autosuggestionService.getMobileUrls
        fn({campaignId : selectedCampaignId}).
            then(data => {
                const redirectionLinks = data.map(d => ({ value : d.id, text: d.name}));
                setRedirectionLinkOptions(redirectionLinks);
                if(redirectionLinks.length === 1){
                    formInstance.setFieldsValue({[FIELD_NAMES.REDIRECTION_LINK] : redirectionLinks[0].value});
                } else {
                    formInstance.setFieldsValue({[FIELD_NAMES.REDIRECTION_LINK] : null});
                }
            })

    }, [redirectionType, selectedCampaignId])

    return (
        <Form
            className="rt--form"
            form={formInstance}
            colon={false}
            requiredMark={false}
            layout="vertical"
            initialValues={formInitialValues}
            onValuesChange={onValuesChange}
        >
            <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name={FIELD_NAMES.NAME}
                        label={`${t('backoffice.campaigns.name')} *`}
                        className='rt--general-form-item'
                        data-placeholder={`${t('backoffice.common.enter')} ${t('backoffice.campaigns.name')}`}
                        rules={[
                            { required: true, whitespace: true, message: t('backoffice.validation.fieldRequired') },
                            { max: 30, message: t('backoffice.validation.fieldInvalid') },
                            { min: 3, message: t('backoffice.validation.fieldInvalid') },
                        ]}
                        validateFirst
                    >
                        <Input
                            placeholder={`${t('backoffice.common.enter')} ${t('backoffice.campaigns.name')}`}
                        />
                    </Form.Item>
                </Col>

                {
                    shouldShowAffiliateField && (
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name={FIELD_NAMES.AFFILIATE_ID}
                                label={`${t('backoffice.campaigns.affiliate')} *`}
                                className={(formInitialValues.affiliateId ? " rt--form-item-disabled" : "")}
                                rules={[
                                    { required: true, message: t('backoffice.validation.fieldRequired') },
                                ]}
                            >
                                <Select
                                    options={affiliates.map(affiliate => ({
                                        value: affiliate.id,
                                        text: affiliate.name
                                    }))}
                                    onChange={onAffiliateChange}
                                    disabled={Boolean(formInitialValues.affiliateId)}
                                    placeholder={`${t('backoffice.common.select')} ${t('backoffice.campaigns.affiliate')}`}
                                    getPopupContainer={() => document.getElementsByClassName("rt--portal-layout")[0]}
                                    search={true}
                                />
                            </Form.Item>
                        </Col>
                    )
                }
            </Row>
            <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name={FIELD_NAMES.CAMPAIGN}
                        label={`${t('backoffice.campaigns.campaign')} *`}
                        className={(disableCampaignField ? " rt--form-item-disabled" : "")}
                        rules={[
                            { required: true, message: t('backoffice.validation.fieldRequired') },
                        ]}
                    >
                        <Select
                            options={campaigns.map(campaign => ({
                                value: campaign.id,
                                text: campaign.name
                            }))}
                            disabled={Boolean(disableCampaignField)}
                            onChange={onCampaignChange}
                            placeholder={`${t('backoffice.common.select')} ${t('backoffice.campaigns.campaign')}`}
                            getPopupContainer={() => document.getElementsByClassName("rt--portal-layout")[0]}
                            search={true}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                    <Form.Item
                        name={FIELD_NAMES.URL_TYPE}
                        label={`${t('backoffice.campaigns.redirectionType')} *`}                       
                        rules={[
                            { required: true, message: t('backoffice.validation.fieldRequired') },
                        ]}
                    >
                        <Select
                            options={
                                [ 
                                    { value: REDIRECTION_TYPE.WEB, text: t('backoffice.campaigns.web') },
                                    { value: REDIRECTION_TYPE.APPS, text: t('backoffice.campaigns.apps') }
                                ]
                            }
                            placeholder={`${t('backoffice.common.select')} ${t('backoffice.campaigns.redirectionType')}`}
                            getPopupContainer={() => document.getElementsByClassName("rt--portal-layout")[0]}
                            onChange={setRedirectionType}
                        />
                    </Form.Item>
                </Col>

                <Col span={24} >
                    <Form.Item
                        name={FIELD_NAMES.REDIRECTION_LINK}
                        label={`${t('backoffice.campaigns.redirectionLink')} *`}                       
                        rules={[
                            { required: true, message: t('backoffice.validation.fieldRequired') },
                        ]}
                        className={disableRedirectionLinkField ? "rt--form-item-disabled" : ""}
                    >
                        <Select
                            disabled={disableRedirectionLinkField}
                            options={redirectionLinkOptions}
                            placeholder={`${t('backoffice.common.select')} ${t('backoffice.campaigns.redirectionLink')}`}
                            getPopupContainer={() => document.getElementsByClassName("rt--portal-layout")[0]}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name={FIELD_NAMES.TRAFFIC_SOURCES}
                        label={t('backoffice.campaigns.trafficSource')}
                        className={
                            (
                                disableTrafficSourceField ? 'rt--form-item-disabled' : ''
                            )
                        }
                    >
                        <Select
                            options={trafficSources.map(source => ({
                                value: source.longId,
                                text: source.name
                            }))}
                            disabled={disableTrafficSourceField}
                            placeholder={`${t('backoffice.common.select')} ${t('backoffice.campaigns.trafficSource')}`}
                            loading={false}
                            isMultiple={true}
                            getPopupContainer={() => document.getElementsByClassName("rt--portal-layout")[0]}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                    <Form.Item
                        name={FIELD_NAMES.TYPE}
                        label={`${t('backoffice.campaigns.mediaType')} *`}
                        className={(formInitialValues.type ? " rt--form-item-disabled" : "")}
                        rules={[
                            { required: true, message: t('backoffice.validation.fieldRequired') },
                        ]}
                    >
                        <Select
                            options={[
                                { value: MEDIA_TYPE.LINK, text: t("backoffice.campaigns.directLink") },
                                { value: MEDIA_TYPE.CREATIVE, text: t("backoffice.campaigns.creative") },
                            ]}
                            placeholder={`${t("backoffice.common.select")} ${t('backoffice.campaigns.mediaType')}`}
                            getPopupContainer={() => document.getElementsByClassName("rt--portal-layout")[0]}
                        />
                    </Form.Item>
                </Col>

                <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue }) => {
                        const selectedMediaType = getFieldValue(FIELD_NAMES.TYPE);
                        const selectedCampaignId = getFieldValue(FIELD_NAMES.CAMPAIGN);

                        if (!Boolean(selectedCampaignId) || selectedMediaType !== MEDIA_TYPE.CREATIVE) {
                            return <></>
                        }

                        if (creatives.length === 0) {
                            return (
                                <Form.Item
                                    name={FIELD_NAMES.CREATIVE}
                                    rules={[
                                        () => ({
                                            validator() {
                                                if (!Boolean(selectedCreativeData)) {
                                                    return Promise.reject(t('backoffice.validation.fieldRequired'))
                                                }

                                                return Promise.resolve();
                                            }
                                        })
                                    ]}
                                >
                                    <i className='rt--text-secondary rt--mt-16 rt--ml-8'>
                                        {t('backoffice.campaigns.noCreativesFound')}
                                    </i>
                                </Form.Item>

                            )
                        }

                        return (
                            <>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name={FIELD_NAMES.CREATIVE}
                                        rules={[
                                            () => ({
                                                validator() {
                                                    if (!Boolean(selectedCreativeData)) {
                                                        return Promise.reject(t('backoffice.validation.fieldRequired'))
                                                    }

                                                    return Promise.resolve();
                                                }
                                            })
                                        ]}
                                    >
                                        <Button
                                            className="rt--button rt--button-main rt--mt-16"
                                            onClick={showLightbox}
                                            disabled={Boolean(formInitialValues.creativeData)}
                                        >
                                            {t("backoffice.campaigns.selectCreative")}
                                        </Button>
                                    </Form.Item>
                                </Col>

                                {Boolean(selectedCreativeData) && (
                                    <Col span={24}>
                                        <Input
                                            readOnly={true}
                                            value={selectedCreativeData.name}
                                        />
                                    </Col>
                                )}
                            </>
                        )
                    }}
                </Form.Item>

                
            </Row>
        </Form>
    );
}

/** MediaCreateForm propTypes
    * PropTypes
*/
MediaCreateForm.propTypes = {
    formInstance: PropTypes.object,
    onCampaignChange: PropTypes.func,
    showLightbox: PropTypes.func,
    selectedCreativeData: PropTypes.object,
    formInitialValues: PropTypes.object,
    campaigns: PropTypes.arrayOf(autoSuggestionType),
    creatives: PropTypes.arrayOf(autoSuggestionType),
    trafficSources: PropTypes.arrayOf(autoSuggestionType),
    onValuesChange: PropTypes.func
}

export default MediaCreateForm;